"use client";

import { useEffect, useState, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import { css } from "styled-system/css";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import { Logo } from "@/components/brand/logo";

const LANDING_SANS = '"IBM Plex Sans", ui-sans-serif, system-ui, sans-serif';
const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

const navItems = [
  { href: "#soluciones", label: "Soluciones" },
  { href: "#rubros",     label: "Rubros" },
  { href: "#planes",     label: "Planes" },
  { href: "#faq",        label: "Preguntas" },
] as const;

export function LandingNavbar() {
  const { user } = useAuth();
  const panelHref = user?.role === "SUPERADMIN" ? "/admin" : "/dashboard";
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled]     = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const onSectionClick = (e: MouseEvent<HTMLAnchorElement>, href: string, close = false) => {
    e.preventDefault();
    const el = document.querySelector(href);
    if (el instanceof HTMLElement) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", href);
    }
    if (close) setMobileOpen(false);
  };

  return (
    <header
      className={css({ position: "sticky", top: 0, zIndex: 50 })}
      style={{
        fontFamily: LANDING_SANS,
        background: "rgba(255,255,255,0.72)",
        backdropFilter: "saturate(180%) blur(14px)",
        WebkitBackdropFilter: "saturate(180%) blur(14px)",
        borderBottom: scrolled ? "1px solid #e7ebf0" : "1px solid transparent",
        boxShadow: scrolled ? "0 1px 20px rgba(15,23,42,0.04)" : "none",
        transition: `border-color .4s ${EASE}, box-shadow .4s ${EASE}`,
      }}
    >
      {/* ── Main bar ── */}
      <div
        className={css({
          maxW: "1120px", mx: "auto",
          px: { base: "5", md: "7" },
          display: "flex", alignItems: "center", justifyContent: "space-between",
          h: "72px",
        })}
      >
        {/* Brand */}
        <Logo
          to="/"
          className={css({ flexShrink: 0 })}
          onClick={(e) => {
            setMobileOpen(false);
            if (window.location.pathname === "/") {
              e.preventDefault();
              window.history.replaceState(null, "", "/");
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
        />

        {/* Desktop nav */}
        <nav
          className={css({ display: { base: "none", md: "flex" }, alignItems: "center", gap: "8" })}
          aria-label="Secciones"
        >
          {navItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className={css({ textDecoration: "none" })}
              style={{
                fontSize: 15, fontWeight: 500, color: "#44506b",
                transition: "color .25s ease",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "#111827"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "#44506b"; }}
              onClick={(e) => onSectionClick(e, item.href)}
            >
              {item.label}
            </a>
          ))}
        </nav>

        {/* Desktop CTAs */}
        <div className={css({ display: { base: "none", md: "flex" }, alignItems: "center", gap: "3.5", flexShrink: 0 })}>
          {user ? (
            <>
              <span
                className={css({ fontSize: "sm", color: "#44506b", fontWeight: "medium" })}
                title={user.email}
              >
                {user.email}
              </span>
              <Link
                to={panelHref}
                className={css({ textDecoration: "none" })}
                style={{
                  display: "inline-flex", alignItems: "center",
                  fontSize: 14, fontWeight: 600, color: "#fff",
                  background: "#111827",
                  padding: "9px 16px", borderRadius: 9,
                  boxShadow: "0 1px 2px rgba(0,0,0,.16)",
                  transition: `transform .3s ${EASE}, box-shadow .3s ease`,
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  el.style.transform = "translateY(-2px)";
                  el.style.boxShadow = "0 12px 28px rgba(15,23,42,.22)";
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  el.style.transform = "";
                  el.style.boxShadow = "0 1px 2px rgba(0,0,0,.16)";
                }}
              >
                Ir al panel
              </Link>
            </>
          ) : (
            <>
              <Link
                to="/auth/login"
                className={css({ textDecoration: "none" })}
                style={{
                  fontSize: 14, fontWeight: 600, color: "#44506b",
                  padding: "9px 16px", borderRadius: 9,
                  transition: "color .25s ease",
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "#111827"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "#44506b"; }}
              >
                Iniciar sesión
              </Link>
              <Link
                to="/auth/register"
                className={css({ textDecoration: "none" })}
                style={{
                  display: "inline-flex", alignItems: "center",
                  fontSize: 14, fontWeight: 600, color: "#fff",
                  background: "#111827",
                  padding: "9px 16px", borderRadius: 9,
                  boxShadow: "0 1px 2px rgba(0,0,0,.16)",
                  transition: `transform .3s ${EASE}, box-shadow .3s ease`,
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  el.style.transform = "translateY(-2px)";
                  el.style.boxShadow = "0 12px 28px rgba(15,23,42,.22)";
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLAnchorElement;
                  el.style.transform = "";
                  el.style.boxShadow = "0 1px 2px rgba(0,0,0,.16)";
                }}
                onMouseDown={(e) => { (e.currentTarget as HTMLAnchorElement).style.transform = "scale(.97)"; }}
                onMouseUp={(e)   => { (e.currentTarget as HTMLAnchorElement).style.transform = ""; }}
              >
                Crear cuenta
              </Link>
            </>
          )}
        </div>

        {/* Mobile hamburger */}
        <button
          type="button"
          className={css({
            display: { base: "flex", md: "none" },
            alignItems: "center", justifyContent: "center",
            w: "10", h: "10", rounded: "xl",
            borderWidth: "1px", borderColor: "#e2e8f0",
            bg: "rgba(255,255,255,0.8)", color: "#334155",
            cursor: "pointer",
            transition: "background .2s, border-color .2s",
            _hover: { bg: "#f8fafc" },
          })}
          aria-expanded={mobileOpen}
          aria-label={mobileOpen ? "Cerrar menú" : "Abrir menú"}
          onClick={() => setMobileOpen((o) => !o)}
        >
          {mobileOpen ? <X size={20} strokeWidth={2} /> : <Menu size={20} strokeWidth={2} />}
        </button>
      </div>

      {/* ── Mobile dropdown ── */}
      {mobileOpen && (
        <div
          className={css({
            display: { base: "block", md: "none" },
            borderTopWidth: "1px", borderColor: "#e7ebf0",
            px: "5", py: "4",
            bg: "rgba(255,255,255,0.96)",
          })}
        >
          <nav className={css({ display: "flex", flexDir: "column", gap: "1" })} aria-label="Secciones móvil">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                className={css({
                  py: "3", px: "3", rounded: "xl",
                  fontSize: "md", fontWeight: "semibold",
                  color: "#334155", textDecoration: "none",
                  bg: "#f8fafc", borderWidth: "1px", borderColor: "#e2e8f0",
                  _hover: { bg: "#f1f5f9", color: "#111827" },
                })}
                onClick={(e) => onSectionClick(e, item.href, true)}
              >
                {item.label}
              </a>
            ))}
          </nav>
          <div className={css({ display: "flex", flexDir: "column", gap: "2", mt: "4", pt: "4", borderTopWidth: "1px", borderColor: "#e2e8f0" })}>
            {user ? (
              <>
                <p className={css({ textAlign: "center", fontSize: "sm", color: "#44506b", py: "1" })}>
                  {user.email}
                </p>
                <Link
                  to={panelHref}
                  className={css({ textAlign: "center", py: "3", rounded: "xl", fontWeight: "bold", color: "white", bg: "#111827", textDecoration: "none" })}
                  onClick={() => setMobileOpen(false)}
                >
                  Ir al panel
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/auth/login"
                  className={css({ textAlign: "center", py: "3", rounded: "xl", fontWeight: "semibold", color: "#334155", borderWidth: "1px", borderColor: "#e2e8f0", textDecoration: "none", bg: "white" })}
                  onClick={() => setMobileOpen(false)}
                >
                  Iniciar sesión
                </Link>
                <Link
                  to="/auth/register"
                  className={css({ textAlign: "center", py: "3", rounded: "xl", fontWeight: "bold", color: "white", bg: "#111827", textDecoration: "none" })}
                  onClick={() => setMobileOpen(false)}
                >
                  Crear cuenta
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
